/**
 * Product Affiliate Settings Page
 * Allows brands to set commission rates and approval settings per product per creator
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Autocomplete,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';

interface Partnership {
  id: string;
  creator_id: string;
  creator: {
    store_name: string;
  };
}

interface Product {
  product_id: string;
  product_name: string;
  price: number;
  main_images: string[];
}

interface AffiliateSetting {
  id: string;
  partnership_id: string;
  product_id: string;
  brand_id: string;
  creator_id: string;
  commission_rate: number;
  commission_type: 'percentage' | 'fixed';
  status: 'approved' | 'rejected' | 'suspended';
  start_date: string | null;
  end_date: string | null;
  max_uses: number | null;
  current_uses: number;
  notes: string | null;
  product: {
    product_name: string;
    price: number;
  };
  partnership: {
    creator: {
      store_name: string;
    };
  };
}

export default function ProductAffiliateSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { productId } = useParams<{ productId?: string }>();
  const navigate = useNavigate();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [loading, setLoading] = useState(true);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [settings, setSettings] = useState<AffiliateSetting[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSetting, setEditingSetting] = useState<AffiliateSetting | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Product-level settings state (when viewing a specific product)
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
  const [isPublicAffiliate, setIsPublicAffiliate] = useState<boolean>(false);
  const [visibilityType, setVisibilityType] = useState<'public' | 'whitelist' | 'private'>('private');
  const [defaultCommissionRate, setDefaultCommissionRate] = useState<number>(10);
  const [defaultCommissionType, setDefaultCommissionType] = useState<'percentage' | 'fixed'>('percentage');
  const [productStartDate, setProductStartDate] = useState<Date | null>(null);
  const [productEndDate, setProductEndDate] = useState<Date | null>(null);
  const [productMaxUses, setProductMaxUses] = useState<number | null>(null);
  const [hasProductDateRange, setHasProductDateRange] = useState(false);
  const [hasProductMaxUses, setHasProductMaxUses] = useState(false);

  // 페이지네이션 상태
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form state for creator-specific settings
  const [selectedPartnership, setSelectedPartnership] = useState<Partnership | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [commissionRate, setCommissionRate] = useState<number>(10);
  const [commissionType, setCommissionType] = useState<'percentage' | 'fixed'>('percentage');
  const [status, setStatus] = useState<'approved' | 'rejected' | 'suspended'>('approved');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [maxUses, setMaxUses] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [hasDateRange, setHasDateRange] = useState(false);
  const [hasMaxUses, setHasMaxUses] = useState(false);
  const [isPublicSetting, setIsPublicSetting] = useState(false);

  useEffect(() => {
    loadBrandProfile();
  }, [user]);

  useEffect(() => {
    if (brandId) {
      Promise.all([loadPartnerships(), loadProducts(), loadSettings()]);
      if (productId) {
        loadProductDetails();
      }
    }
  }, [brandId, productId]);

  const loadBrandProfile = async () => {
    if (!user) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', profile.id)
        .single();

      if (sellerError) throw sellerError;

      setBrandId(sellerProfile.id);
    } catch (err: any) {
      setError('브랜드 프로필을 불러올 수 없습니다: ' + err.message);
    }
  };

  const loadPartnerships = async () => {
    try {
      // First, get partnerships
      const { data: partnershipsData, error: partnershipsError } = await supabase
        .from('affiliate_partnerships')
        .select('id, creator_id')
        .eq('brand_id', brandId)
        .eq('status', 'active');

      if (partnershipsError) throw partnershipsError;

      if (!partnershipsData || partnershipsData.length === 0) {
        setPartnerships([]);
        return;
      }

      // Get unique creator IDs
      const creatorIds = Array.from(new Set(partnershipsData.map((p) => p.creator_id)));

      // Fetch creator info from seller_profile_public view
      const { data: creatorsData, error: creatorsError } = await supabase
        .from('seller_profile_public')
        .select('id, store_name')
        .in('id', creatorIds);

      if (creatorsError) {
        console.error('Error fetching creators:', creatorsError);
      }

      // Map creators by ID for quick lookup
      const creatorsMap = new Map(
        (creatorsData || []).map((c) => [c.id, { store_name: c.store_name }])
      );

      // Combine partnerships with creator info
      const transformedData = partnershipsData.map((partnership) => ({
        id: partnership.id,
        creator_id: partnership.creator_id,
        creator: creatorsMap.get(partnership.creator_id) || { store_name: '알 수 없음' },
      }));

      setPartnerships(transformedData);
    } catch (err: any) {
      setError('파트너십 목록을 불러올 수 없습니다: ' + err.message);
    }
  };

  const loadProducts = async () => {
    try {
      console.log('🔍 loadProducts - brandId:', brandId);

      // 먼저 seller_id 없이 모든 active 상품 조회해서 디버깅
      const { data: allProducts, error: allError } = await supabase
        .from('products')
        .select('product_id, product_name, seller_id, status')
        .eq('status', 'active')
        .limit(5);

      console.log('🔍 모든 active 상품 샘플:', allProducts);

      const { data, error } = await supabase
        .from('products')
        .select('product_id, product_name, price, main_images')
        .eq('seller_id', brandId)
        .eq('status', 'active');

      console.log('🔍 내 상품 조회 결과:', data, 'error:', error);

      if (error) throw error;

      setProducts(data || []);
    } catch (err: any) {
      setError('제품 목록을 불러올 수 없습니다: ' + err.message);
    }
  };

  const loadProductDetails = async () => {
    if (!productId || !brandId) return;

    try {
      const { data, error } = await supabase
        .from('products')
        .select('product_id, product_name, price, main_images, is_public_affiliate, default_commission_rate, default_commission_type')
        .eq('product_id', productId)
        .eq('seller_id', brandId)
        .single();

      if (error) throw error;

      if (data) {
        setCurrentProduct(data);
        setIsPublicAffiliate(data.is_public_affiliate || false);
        // Determine visibility type based on current settings
        setVisibilityType(data.is_public_affiliate ? 'public' : 'private');
        setDefaultCommissionRate(data.default_commission_rate || 10);
        setDefaultCommissionType(data.default_commission_type || 'percentage');
      }
    } catch (err: any) {
      setError('제품 정보를 불러올 수 없습니다: ' + err.message);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);

      // Build query
      let query = supabase
        .from('product_affiliate_settings')
        .select(
          `
          *,
          product:products(product_name, price)
        `
        )
        .eq('brand_id', brandId);

      // If viewing a specific product, filter by product_id
      if (productId) {
        query = query.eq('product_id', productId);
      }

      const { data: settingsData, error: settingsError } = await query.order('created_at', { ascending: false });

      if (settingsError) throw settingsError;

      if (!settingsData || settingsData.length === 0) {
        setSettings([]);
        return;
      }

      // Get unique partnership IDs (filter out nulls for public settings)
      const partnershipIds = Array.from(new Set(settingsData.map((s: any) => s.partnership_id).filter((id) => id !== null)));

      let partnershipsData: any[] = [];
      let creatorsData: any[] = [];

      // Only fetch partnerships if there are non-null partnership IDs
      if (partnershipIds.length > 0) {
        // Fetch partnerships
        const { data, error: partnershipsError } = await supabase
          .from('affiliate_partnerships')
          .select('id, creator_id')
          .in('id', partnershipIds);

        if (partnershipsError) {
          console.error('Error fetching partnerships:', partnershipsError);
        } else {
          partnershipsData = data || [];
        }

        // Get creator IDs from partnerships
        const creatorIds = Array.from(new Set(partnershipsData.map((p) => p.creator_id)));

        if (creatorIds.length > 0) {
          // Fetch creator info from seller_profile_public view
          const { data, error: creatorsError } = await supabase
            .from('seller_profile_public')
            .select('id, store_name')
            .in('id', creatorIds);

          if (creatorsError) {
            console.error('Error fetching creators:', creatorsError);
          } else {
            creatorsData = data || [];
          }
        }
      }

      // Create maps for quick lookup
      const creatorsMap = new Map(
        creatorsData.map((c) => [c.id, { store_name: c.store_name }])
      );

      const partnershipsMap = new Map(
        partnershipsData.map((p) => [
          p.id,
          {
            creator: creatorsMap.get(p.creator_id) || { store_name: '알 수 없음' },
          },
        ])
      );

      // Combine settings with partnership and creator info
      const combinedSettings = settingsData.map((setting: any) => ({
        ...setting,
        partnership: setting.is_public
          ? { creator: { store_name: '공개' } }
          : (partnershipsMap.get(setting.partnership_id) || {
              creator: { store_name: '알 수 없음' },
            }),
      }));

      setSettings(combinedSettings);
    } catch (err: any) {
      setError('설정 목록을 불러올 수 없습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (setting?: AffiliateSetting) => {
    if (setting) {
      // Editing existing setting
      setEditingSetting(setting);
      const partnership = partnerships.find((p) => p.id === setting.partnership_id);
      const product = products.find((p) => p.product_id === setting.product_id);

      setSelectedPartnership(partnership || null);
      setSelectedProduct(product || null);
      setCommissionRate(setting.commission_rate);
      setCommissionType(setting.commission_type);
      setStatus(setting.status);
      setStartDate(setting.start_date ? new Date(setting.start_date) : null);
      setEndDate(setting.end_date ? new Date(setting.end_date) : null);
      setMaxUses(setting.max_uses);
      setNotes(setting.notes || '');
      setHasDateRange(!!setting.start_date || !!setting.end_date);
      setHasMaxUses(setting.max_uses !== null);
    } else {
      // Creating new setting
      resetForm();
      // If viewing a specific product, auto-select it
      if (productId && currentProduct) {
        setSelectedProduct(currentProduct);
      }
    }
    setOpenDialog(true);
  };

  const resetForm = () => {
    setEditingSetting(null);
    setSelectedPartnership(null);
    setSelectedProduct(null);
    setCommissionRate(10);
    setCommissionType('percentage');
    setStatus('approved');
    setStartDate(null);
    setEndDate(null);
    setMaxUses(null);
    setNotes('');
    setHasDateRange(false);
    setHasMaxUses(false);
    setIsPublicSetting(false);
  };

  const handleSave = async () => {
    // 공개 설정이 아닐 때는 파트너 선택 필수 (새로 만들 때만)
    if (!editingSetting && !isPublicSetting && !selectedPartnership) {
      setError('크리에이터를 선택해주세요');
      return;
    }

    // 새로 만들 때만 제품 선택 필수
    if (!editingSetting && !selectedProduct) {
      setError('제품을 선택해주세요');
      return;
    }

    if (!brandId) {
      setError('브랜드 정보가 없습니다');
      return;
    }

    if (commissionType === 'percentage' && (commissionRate < 0 || commissionRate > 100)) {
      setError('퍼센트 커미션은 0-100 사이여야 합니다');
      return;
    }

    try {
      if (editingSetting) {
        // Update existing - only update editable fields
        const updateData = {
          commission_rate: commissionRate,
          commission_type: commissionType,
          status,
          start_date: hasDateRange && startDate ? startDate.toISOString() : null,
          end_date: hasDateRange && endDate ? endDate.toISOString() : null,
          max_uses: hasMaxUses ? maxUses : null,
          notes: notes || null,
        };

        const { error } = await supabase
          .from('product_affiliate_settings')
          .update(updateData)
          .eq('id', editingSetting.id);

        if (error) throw error;

        setSuccess('설정이 업데이트되었습니다');
      } else {
        // Create new
        const settingData = {
          partnership_id: isPublicSetting ? null : selectedPartnership!.id,
          product_id: selectedProduct!.product_id,
          brand_id: brandId,
          creator_id: isPublicSetting ? null : selectedPartnership!.creator_id,
          commission_rate: commissionRate,
          commission_type: commissionType,
          status,
          start_date: hasDateRange && startDate ? startDate.toISOString() : null,
          end_date: hasDateRange && endDate ? endDate.toISOString() : null,
          max_uses: hasMaxUses ? maxUses : null,
          notes: notes || null,
          is_public: isPublicSetting,
        };

        const { error } = await supabase.from('product_affiliate_settings').insert(settingData);

        if (error) throw error;

        setSuccess('설정이 생성되었습니다');
      }

      setOpenDialog(false);
      resetForm();
      loadSettings();
    } catch (err: any) {
      setError('저장 실패: ' + err.message);
    }
  };

  const handleDelete = async (settingId: string) => {
    if (!window.confirm('이 설정을 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase.from('product_affiliate_settings').delete().eq('id', settingId);

      if (error) throw error;

      setSuccess('설정이 삭제되었습니다');
      loadSettings();
    } catch (err: any) {
      setError('삭제 실패: ' + err.message);
    }
  };

  const handleSaveProductSettings = async () => {
    if (!productId || !brandId) {
      setError('제품 정보가 없습니다');
      return;
    }

    if (defaultCommissionType === 'percentage' && (defaultCommissionRate < 0 || defaultCommissionRate > 100)) {
      setError('퍼센트 커미션은 0-100 사이여야 합니다');
      return;
    }

    try {
      const updateData: any = {
        is_public_affiliate: visibilityType === 'public',
        default_commission_rate: defaultCommissionRate,
        default_commission_type: defaultCommissionType,
      };

      const { error } = await supabase
        .from('products')
        .update(updateData)
        .eq('product_id', productId)
        .eq('seller_id', brandId);

      if (error) throw error;

      setSuccess('제품 설정이 저장되었습니다');
      loadProductDetails();
    } catch (err: any) {
      setError('저장 실패: ' + err.message);
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      approved: { label: '승인됨', color: 'success' as const },
      rejected: { label: '거절됨', color: 'error' as const },
      suspended: { label: '일시정지', color: 'warning' as const },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  // 페이지네이션 적용
  const paginatedSettings = settings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // 페이지네이션 핸들러
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Render product-level settings view
  if (productId && currentProduct) {
    return (
      <Box sx={styles.pageContainer}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/seller/products')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" fontWeight="bold" sx={styles.title}>
              {currentProduct.product_name} - 어필리에이트 설정
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={styles.text}>
              {currentProduct.price.toLocaleString()}원
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Product-level public visibility settings */}
        <Card sx={{ ...styles.paper, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ ...styles.text, mb: 2 }}>
              공개 설정
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                value={visibilityType}
                onChange={(e) => setVisibilityType(e.target.value as 'public' | 'whitelist' | 'private')}
              >
                <FormControlLabel
                  value="public"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography sx={styles.text}>전체 공개 (마켓플레이스)</Typography>
                      <Typography variant="caption" color="text.secondary" sx={styles.text}>
                        모든 크리에이터가 마켓플레이스에서 이 제품을 볼 수 있습니다
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="whitelist"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography sx={styles.text}>특정 크리에이터만</Typography>
                      <Typography variant="caption" color="text.secondary" sx={styles.text}>
                        아래 설정한 크리에이터만 이 제품을 사용할 수 있습니다
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="private"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography sx={styles.text}>비공개 (요청 시에만)</Typography>
                      <Typography variant="caption" color="text.secondary" sx={styles.text}>
                        마켓플레이스에 표시되지 않으며, 크리에이터 요청 시 수동 승인합니다
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight="bold" sx={{ ...styles.text, mb: 2 }}>
              기본 커미션 설정
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  type="number"
                  label="기본 커미션율/금액"
                  value={defaultCommissionRate}
                  onChange={(e) => setDefaultCommissionRate(Number(e.target.value))}
                  required
                  sx={styles.input}
                  helperText="크리에이터별 설정이 없을 경우 적용됩니다"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel sx={styles.text}>타입</InputLabel>
                  <Select
                    value={defaultCommissionType}
                    onChange={(e) => setDefaultCommissionType(e.target.value as any)}
                    label="타입"
                    sx={styles.input}
                    MenuProps={{ PaperProps: { sx: styles.selectMenu } }}
                  >
                    <MenuItem value="percentage" sx={styles.menuItem}>
                      퍼센트 (%)
                    </MenuItem>
                    <MenuItem value="fixed" sx={styles.menuItem}>
                      고정 금액 (원)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" onClick={handleSaveProductSettings} startIcon={<SaveIcon />}>
                저장
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Creator-specific settings for this product */}
        <Card sx={{ ...styles.paper, mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={styles.text}>
                크리에이터별 개별 설정
              </Typography>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                크리에이터 추가
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>크리에이터</TableCell>
                      <TableCell>커미션</TableCell>
                      <TableCell>상태</TableCell>
                      <TableCell>기간</TableCell>
                      <TableCell>사용 횟수</TableCell>
                      <TableCell align="right">액션</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary" sx={styles.text}>
                            크리에이터별 개별 설정이 없습니다. 기본 커미션이 적용됩니다.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedSettings.map((setting) => (
                        <TableRow key={setting.id}>
                          <TableCell>
                            <Typography fontWeight="bold" sx={styles.text}>
                              {setting.partnership.creator.store_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {setting.commission_type === 'percentage'
                              ? `${setting.commission_rate}%`
                              : `${setting.commission_rate.toLocaleString()}원`}
                          </TableCell>
                          <TableCell>{getStatusChip(setting.status)}</TableCell>
                          <TableCell>
                            {setting.start_date || setting.end_date ? (
                              <Typography variant="body2" sx={styles.text}>
                                {setting.start_date && new Date(setting.start_date).toLocaleDateString()}
                                {' ~ '}
                                {setting.end_date ? new Date(setting.end_date).toLocaleDateString() : '무제한'}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={styles.text}>
                                무제한
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {setting.max_uses ? (
                              <Typography variant="body2" sx={styles.text}>
                                {setting.current_uses} / {setting.max_uses}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={styles.text}>
                                무제한
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleOpenDialog(setting)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(setting.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={settings.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="페이지당 행:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                />
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Settings Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: styles.paper }}>
          <DialogTitle sx={styles.text}>{editingSetting ? '설정 수정' : '크리에이터 추가'}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Autocomplete
                options={partnerships}
                getOptionLabel={(option) => option.creator.store_name}
                value={selectedPartnership}
                onChange={(_, value) => setSelectedPartnership(value)}
                renderInput={(params) => <TextField {...params} label="크리에이터 선택" required sx={styles.input} />}
                disabled={!!editingSetting}
                sx={{ mb: 2 }}
              />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    type="number"
                    label="커미션율/금액"
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(Number(e.target.value))}
                    required
                    sx={styles.input}
                    helperText="이 크리에이터에게만 적용되는 특별 커미션"
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel sx={styles.text}>타입</InputLabel>
                    <Select
                      value={commissionType}
                      onChange={(e) => setCommissionType(e.target.value as any)}
                      label="타입"
                      sx={styles.input}
                      MenuProps={{ PaperProps: { sx: styles.selectMenu } }}
                    >
                      <MenuItem value="percentage" sx={styles.menuItem}>
                        퍼센트 (%)
                      </MenuItem>
                      <MenuItem value="fixed" sx={styles.menuItem}>
                        고정 금액 (원)
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={styles.text}>상태</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                  label="상태"
                  sx={styles.input}
                  MenuProps={{ PaperProps: { sx: styles.selectMenu } }}
                >
                  <MenuItem value="approved" sx={styles.menuItem}>
                    승인됨
                  </MenuItem>
                  <MenuItem value="rejected" sx={styles.menuItem}>
                    거절됨
                  </MenuItem>
                  <MenuItem value="suspended" sx={styles.menuItem}>
                    일시정지
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={<Switch checked={hasDateRange} onChange={(e) => setHasDateRange(e.target.checked)} />}
                label="게시 기간 설정"
                sx={{ ...styles.text, mb: 1 }}
              />

              {hasDateRange && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="시작 날짜"
                      value={
                        startDate
                          ? new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
                          : ''
                      }
                      onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                      InputLabelProps={{ shrink: true }}
                      sx={styles.input}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="종료 날짜"
                      value={
                        endDate ? new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''
                      }
                      onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                      InputLabelProps={{ shrink: true }}
                      sx={styles.input}
                    />
                  </Grid>
                </Grid>
              )}

              <FormControlLabel
                control={<Switch checked={hasMaxUses} onChange={(e) => setHasMaxUses(e.target.checked)} />}
                label="최대 사용 횟수 제한"
                sx={{ ...styles.text, mb: 1 }}
              />

              {hasMaxUses && (
                <TextField
                  fullWidth
                  type="number"
                  label="최대 사용 횟수"
                  value={maxUses || ''}
                  onChange={(e) => setMaxUses(Number(e.target.value))}
                  sx={{ ...styles.input, mb: 2 }}
                />
              )}

              <TextField
                fullWidth
                multiline
                rows={3}
                label="메모 (선택사항)"
                placeholder="설정에 대한 메모를 입력하세요"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={styles.input}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>{t("common.cancel")}</Button>
            <Button variant="contained" onClick={handleSave} startIcon={<SaveIcon />}>
              저장
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Render list view (all products)
  return (
    <Box sx={styles.pageContainer}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" sx={styles.title}>
            제품별 어필리에이트 설정
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            새 설정 추가
          </Button>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Card sx={{ ...styles.paper, mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" sx={styles.text}>{settings.length}</Typography>
                <Typography variant="body2" color="text.secondary" sx={styles.text}>
                  총 설정 수
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" sx={styles.text}>{settings.filter((s) => s.status === 'approved').length}</Typography>
                <Typography variant="body2" color="text.secondary" sx={styles.text}>
                  활성 설정
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" sx={styles.text}>{partnerships.length}</Typography>
                <Typography variant="body2" color="text.secondary" sx={styles.text}>
                  활성 파트너
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ ...styles.paper, ...styles.tableContainer }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>크리에이터</TableCell>
                  <TableCell>제품</TableCell>
                  <TableCell>커미션</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell>기간</TableCell>
                  <TableCell>사용 횟수</TableCell>
                  <TableCell align="right">액션</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {settings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary" sx={styles.text}>설정이 없습니다</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedSettings.map((setting) => (
                    <TableRow key={setting.id}>
                      <TableCell>
                        <Typography fontWeight="bold" sx={styles.text}>{setting.partnership.creator.store_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={styles.text}>{setting.product.product_name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={styles.text}>
                          {setting.product.price.toLocaleString()}원
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {setting.commission_type === 'percentage'
                          ? `${setting.commission_rate}%`
                          : `${setting.commission_rate.toLocaleString()}원`}
                      </TableCell>
                      <TableCell>{getStatusChip(setting.status)}</TableCell>
                      <TableCell>
                        {setting.start_date || setting.end_date ? (
                          <Typography variant="body2" sx={styles.text}>
                            {setting.start_date && new Date(setting.start_date).toLocaleDateString()}
                            {' ~ '}
                            {setting.end_date ? new Date(setting.end_date).toLocaleDateString() : '무제한'}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={styles.text}>
                            무제한
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {setting.max_uses ? (
                          <Typography variant="body2" sx={styles.text}>
                            {setting.current_uses} / {setting.max_uses}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={styles.text}>
                            무제한
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenDialog(setting)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(setting.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={settings.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="페이지당 행:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
          </TableContainer>
        )}

        {/* Settings Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: styles.paper }}>
          <DialogTitle sx={styles.text}>{editingSetting ? '설정 수정' : '새 설정 추가'}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Autocomplete
                    options={partnerships}
                    getOptionLabel={(option) => option.creator.store_name}
                    value={selectedPartnership}
                    onChange={(e, value) => setSelectedPartnership(value)}
                    renderInput={(params) => <TextField {...params} label="크리에이터 선택" required sx={styles.input} />}
                    disabled={!!editingSetting || isPublicSetting}
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isPublicSetting}
                      onChange={(e) => {
                        setIsPublicSetting(e.target.checked);
                        if (e.target.checked) {
                          setSelectedPartnership(null);
                        }
                      }}
                      disabled={!!editingSetting}
                    />
                  }
                  label="공개"
                  sx={{ ...styles.text, ml: 2, minWidth: '100px' }}
                />
              </Box>

              <Autocomplete
                options={products}
                getOptionLabel={(option) => `${option.product_name} (${option.price.toLocaleString()}원)`}
                value={selectedProduct}
                onChange={(e, value) => setSelectedProduct(value)}
                renderInput={(params) => <TextField {...params} label="제품 선택" required sx={styles.input} />}
                disabled={!!editingSetting}
                sx={{ mb: 2 }}
              />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    type="number"
                    label="커미션율/금액"
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(Number(e.target.value))}
                    required
                    sx={styles.input}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel sx={styles.text}>타입</InputLabel>
                    <Select value={commissionType} onChange={(e) => setCommissionType(e.target.value as any)} label="타입" sx={styles.input} MenuProps={{ PaperProps: { sx: styles.selectMenu } }}>
                      <MenuItem value="percentage" sx={styles.menuItem}>퍼센트 (%)</MenuItem>
                      <MenuItem value="fixed" sx={styles.menuItem}>고정 금액 (원)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={styles.text}>상태</InputLabel>
                <Select value={status} onChange={(e) => setStatus(e.target.value as any)} label="상태" sx={styles.input} MenuProps={{ PaperProps: { sx: styles.selectMenu } }}>
                  <MenuItem value="approved" sx={styles.menuItem}>승인됨</MenuItem>
                  <MenuItem value="rejected" sx={styles.menuItem}>거절됨</MenuItem>
                  <MenuItem value="suspended" sx={styles.menuItem}>일시정지</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={<Switch checked={hasDateRange} onChange={(e) => setHasDateRange(e.target.checked)} />}
                label="게시 기간 설정"
                sx={{ ...styles.text, mb: 1 }}
              />

              {hasDateRange && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="시작 날짜"
                      value={startDate ? new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                      InputLabelProps={{ shrink: true }}
                      sx={styles.input}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="종료 날짜"
                      value={endDate ? new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                      InputLabelProps={{ shrink: true }}
                      sx={styles.input}
                    />
                  </Grid>
                </Grid>
              )}

              <FormControlLabel
                control={<Switch checked={hasMaxUses} onChange={(e) => setHasMaxUses(e.target.checked)} />}
                label="최대 사용 횟수 제한"
                sx={{ ...styles.text, mb: 1 }}
              />

              {hasMaxUses && (
                <TextField
                  fullWidth
                  type="number"
                  label="최대 사용 횟수"
                  value={maxUses || ''}
                  onChange={(e) => setMaxUses(Number(e.target.value))}
                  sx={{ ...styles.input, mb: 2 }}
                />
              )}

              <TextField
                fullWidth
                multiline
                rows={3}
                label="메모 (선택사항)"
                placeholder="설정에 대한 메모를 입력하세요"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={styles.input}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>{t("common.cancel")}</Button>
            <Button variant="contained" onClick={handleSave} startIcon={<SaveIcon />}>
              저장
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
}
